import { inject, Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { EmployeeService } from 'src/app/services/employee.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UtilsService } from 'src/app/services/utils.service';

@Injectable({ providedIn: 'root' })
export class RrhhExtraHoursGuard implements CanActivate {
  private readonly permissions = inject(PermissionsService);
  private readonly employee = inject(EmployeeService);
  private readonly utils = inject(UtilsService);
  private readonly router = inject(Router);

  async canActivate(): Promise<boolean | UrlTree> {
    if (await this.canAccessAsRrhhOrAdmin()) {
      return true;
    }

    if (this.isExtraHoursResponsible()) {
      return true;
    }

    await this.waitUntilAccessContextIsReady();

    if (await this.canAccessAsRrhhOrAdmin()) {
      return true;
    }

    if (this.isExtraHoursResponsible()) {
      return true;
    }

    if (this.permissions.isAccountManager()) {
      return true;
    }

    return this.router.createUrlTree(['/']);
  }

  private async canAccessAsRrhhOrAdmin(attempt = 0): Promise<boolean> {
    if (this.permissions.validateDepartment(['direction', 'rrhh'])) {
      return true;
    }

    if (attempt >= 5) {
      return false;
    }

    await this.utils.sleep(200);

    return this.canAccessAsRrhhOrAdmin(attempt + 1);
  }

  private isExtraHoursResponsible(): boolean {
    return this.employee.actionsRequired().is_extra_hours_responsible;
  }

  private async waitUntilAccessContextIsReady(attempt = 0): Promise<void> {
    if (attempt >= 30) {
      return;
    }

    if (this.permissions.fetching_my_company_product_roles != null) {
      try {
        await this.permissions.fetching_my_company_product_roles;
      } catch {
        return;
      }
    }

    const is_access_context_ready =
      this.isExtraHoursResponsible() ||
      this.permissions.validateDepartment(['direction', 'rrhh']) ||
      this.permissions.isAccountManager() ||
      this.permissions.areCompanyProductUserMemberInitiated();

    if (is_access_context_ready) {
      return;
    }

    await this.utils.sleep(100);

    return this.waitUntilAccessContextIsReady(attempt + 1);
  }
}
